import { engine } from '@play-co/timestep-core';
import {
  EventRemover,
  addPubSubListener,
} from '@play-co/timestep-core/lib/lib/PubSub';
import { addDOMEventListener } from '@play-co/timestep-core/lib/platforms/browser/Input';
import isVisible from '@play-co/timestep-core/lib/ui/utils/isVisible';
import { Matrix2D, RectShape } from '@play-co/timestep-core/math';
import { RenderContext } from '@play-co/timestep-core/renderer';
import {
  ViewBacking,
  View,
  ReflowReason,
  ViewOpts,
  getScreenPosition,
} from '@play-co/timestep-core/ui';

const inputStyleDefaults = `
  background: none;
  border: none;
  outline: none;
  position: absolute;
  padding: 0;
  margin: 0;
  transform-origin: 0 0;
  line-height: 1;
`;

class TextInputViewBacking extends ViewBacking {
  _view: TextInputView;

  private _cachedTransform = new Matrix2D();
  private _cachedOpacity = '';

  constructor(view: View) {
    super(view);
    this._view = view as TextInputView;
  }

  _onVisible() {
    // @ts-ignore
    super._onVisible();

    this._view.handleVisiblityChange();
  }

  _onResize(reason: ReflowReason) {
    // @ts-ignore
    super._onResize(reason);

    this._view.handleTransformUpdate();
  }

  updateGlobalTransform(parentTransform: Matrix2D) {
    // @ts-ignore
    super.updateGlobalTransform(parentTransform);
    // @ts-ignore
    if (!this._cachedTransform.isEqualTo(this._globalTransform)) {
      // @ts-ignore
      this._cachedTransform.copy(this._globalTransform);
      this._view.handleTransformUpdate();
    }
  }

  wrapRender(
    ctx: RenderContext,
    parentTransform: Matrix2D,
    parentOpacity: number,
  ): void {
    super.wrapRender(ctx, parentTransform, parentOpacity);

    const globalOpacity = parentOpacity * this.opacity;

    // CSS opacity must be set as a string for some reason.
    const opacityStr = String(globalOpacity);
    if (opacityStr !== this._cachedOpacity) {
      this._view.input.style.opacity = opacityStr;
      this._cachedOpacity = opacityStr;
    }
  }
}

export interface TextInputViewOpts extends ViewOpts {
  /**
   * CSS class to apply to the input.
   */
  inputClass?: string;

  /**
   * Inline styles to apply to the input.
   */
  inputStyle?: Partial<CSSStyleDeclaration>;

  /**
   * Multiline input.
   */
  multiline?: boolean;
}

/**
 * Renders a native HTML input positioned above the view. Useful for opening the
 * on-screen keyboard on mobile devices.
 *
 * Use [[TextInputView.input]] to get the value or to bind native DOM events.
 */
export default class TextInputView extends View {
  protected parentEl: HTMLElement;
  protected inputEl: HTMLInputElement | HTMLTextAreaElement;
  protected canvasEl: HTMLCanvasElement;
  protected canvasBounds: DOMRect;
  protected canvasEvents: EventRemover[];
  protected zIndex: number;
  protected inputRect: RectShape;
  protected isAttached: boolean;
  protected visibilityEvents: EventRemover[];
  protected visibilityCallback: Function;

  constructor(opts: TextInputViewOpts = {}) {
    opts.Backing = TextInputViewBacking;

    super(opts);

    const canvas = engine.getCanvas();
    if (!canvas) {
      throw new Error('Engine is not attached to canvas');
    }

    const parentEl = canvas.parentElement;
    if (!parentEl) {
      throw new Error('Canvas is not attached to DOM tree');
    }

    this.parentEl = parentEl;
    this.inputEl = document.createElement(
      opts.multiline ? 'textarea' : 'input',
    );
    this.canvasEl = canvas;
    this.canvasBounds = canvas.getBoundingClientRect();
    this.canvasEvents = [];

    const canvasStyle = window.getComputedStyle(canvas, null);
    const canvasZIndex = parseInt(canvasStyle.zIndex);

    this.zIndex = isNaN(canvasZIndex) ? 1 : canvasZIndex + 1;
    this.inputRect = { x: 0, y: 0, width: 0, height: 0 };
    this.isAttached = false;
    this.visibilityEvents = [];
    this.visibilityCallback = () => this.handleVisiblityChange();

    if (opts) {
      this.updateTextInputViewOpts(opts);
    }

    if (!opts?.inputStyle) {
      // Apply style defaults, if we don't have any user styles.
      this.inputStyle = {};
    }

    this.on('ViewAdded', () => this.handleAdded());
    this.on('ViewRemoved', () => this.handleRemoved());

    this.inputEl.addEventListener('focus', () => this.handleInputFocus());
    this.inputEl.addEventListener('blur', () => this.handleInputBlur());

    if (opts.superview?.getApp()) {
      // `ViewAdded` event can't be triggered at this point, so if we're
      // attching the view to the graph, handle it manually.
      this.handleAdded();
    }

    this.handleVisiblityChange();
    this.handleTransformUpdate();
  }

  updateOpts(opts: TextInputViewOpts) {
    opts = super.updateOpts(opts) as TextInputViewOpts;
    return this.updateTextInputViewOpts(opts);
  }

  private updateTextInputViewOpts(opts: TextInputViewOpts): TextInputViewOpts {
    if (typeof opts.inputClass === 'string') {
      this.inputEl.className = opts.inputClass;
    }

    if (opts.inputStyle) {
      this.inputStyle = opts.inputStyle;
    }

    return opts;
  }

  protected handleAdded(): void {
    let superview = this.getSuperview();

    while (superview) {
      this.visibilityEvents.push(
        addPubSubListener(
          superview,
          'VisibilityChanged',
          this.visibilityCallback,
        ),
      );

      superview = superview.getSuperview();
    }

    this.handleVisiblityChange();
  }

  protected handleRemoved(): void {
    for (const removeEvent of this.visibilityEvents) {
      removeEvent();
    }

    this.visibilityEvents.length = 0;

    this.handleVisiblityChange();
  }

  protected handleInputFocus(): void {
    this.setPageDimentionsFrozen(true);

    // By default, timestep's input handling eats up events and prevents `blur`
    // event from naturally occurring for the input element. The following code
    // sets up additional listeners to detect when the focus is shifted to
    // canvas and triggers `blur` event.
    const cb = () => this.inputEl.blur();

    this.canvasEvents.push(
      addDOMEventListener(this.canvasEl, 'touchstart', cb),
      addDOMEventListener(this.canvasEl, 'mousedown', cb),
    );
  }

  protected handleInputBlur(): void {
    this.setPageDimentionsFrozen(false);

    for (const removeEvent of this.canvasEvents) {
      removeEvent();
    }

    this.canvasEvents.length = 0;
  }

  protected attachInput(): void {
    if (this.isAttached) {
      return;
    }

    this.isAttached = true;

    const result = this.canvasEl.insertAdjacentElement(
      'afterend',
      this.inputEl,
    );

    if (!result) {
      if (process.env.IS_DEVELOPMENT) {
        console.error('Failed to insert input element');
      }
    }
  }

  protected detachInput(): void {
    if (!this.isAttached) {
      return;
    }

    this.isAttached = false;
    this.parentEl.removeChild(this.inputEl);
  }

  /**
   * @internal
   */
  handleVisiblityChange(): void {
    if (isVisible(this)) {
      this.attachInput();
    } else {
      this.detachInput();
    }
  }

  /**
   * @internal
   */
  handleTransformUpdate(): void {
    if (!this.inputEl) {
      return;
    }

    const cStyle = window.getComputedStyle(this.inputEl);

    // N.B. Computed style values are always going to be in pixels, even if they
    // were set using different units.
    const pTop = parseInt(cStyle.paddingTop) || 0;
    const pBottom = parseInt(cStyle.paddingBottom) || 0;
    const pLeft = parseInt(cStyle.paddingLeft) || 0;
    const pRight = parseInt(cStyle.paddingRight) || 0;
    const bTop = parseInt(cStyle.borderTopWidth) || 0;
    const bBottom = parseInt(cStyle.borderBottomWidth) || 0;
    const bLeft = parseInt(cStyle.borderLeftWidth) || 0;
    const bRight = parseInt(cStyle.borderRightWidth) || 0;

    const oTop = pTop + bTop;
    const oBottom = pBottom + bBottom;
    const oLeft = pLeft + bLeft;
    const oRight = pRight + bRight;

    const rect = getScreenPosition(this, this.inputRect);
    const elStyle = this.inputEl.style;
    elStyle.left = rect.x + 'px';
    elStyle.top = rect.y + 'px';
    elStyle.width = rect.width - oLeft - oRight + 'px';
    elStyle.height = rect.height - oTop - oBottom + 'px';
  }

  get input(): HTMLTextAreaElement | HTMLInputElement {
    return this.inputEl;
  }

  get inputClass(): string {
    return this.inputEl.className;
  }

  set inputClass(className: string) {
    this.inputEl.className = className;
  }

  set inputStyle(style: Partial<CSSStyleDeclaration>) {
    const elStyle = this.inputEl.style;
    const opacity = elStyle.opacity;
    elStyle.cssText = inputStyleDefaults;
    elStyle.zIndex = String(this.zIndex);
    elStyle.opacity = opacity;

    for (const propName in style) {
      // eslint-disable-next-line no-prototype-builtins
      if (style.hasOwnProperty(propName)) {
        elStyle[propName] = style[propName] as string;
      }
    }

    this.handleTransformUpdate();
  }

  private setPageDimentionsFrozen(frozen: boolean): void {
    const { innerWidth, innerHeight } = window;
    const viewport = document.querySelector('meta[name=viewport]');
    viewport.setAttribute(
      'content',
      `user-scalable=no,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,shrink-to-fit=no,width=${
        frozen ? innerWidth : 'device-width'
      },height=${frozen ? innerHeight : 'device-height'}`,
    );
  }
}
